import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowRight, Shield, Users, Globe, Scale, FileText } from 'lucide-react';

export default function About() {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
            About <span className="text-accent">BULLAALE AS</span> and <span className="text-accent">Ezy Transfer</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Revolutionizing money transfer through compliant, secure, and scalable digital solutions
          </p>
        </div>

        {/* Main Content */}
        <Card className="p-8 md:p-12 mb-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-lg leading-relaxed mb-6">
              Bullaale AS together with its partners is in the process of developing a comprehensive money transfer technology to integrate and offer the digitization of Money Transfer. Currently the ecosystem of money transfer in most countries is extra-judicial and does not comply with any regulations, standards and rules and are often subject to closure or collapses and have no ability to scale or address the evolving regulatory and legal ecosystem.
            </p>
            
            <p className="text-lg leading-relaxed mb-6">
              Moreover, the inherent risk that is provincial in the system has not been ably or adequately addressed but have exacerbated with the recent development in the regulatory environment and in particular - AML/CFT and cash handling and all other activities involved in cash. One just needs to look at the recent history of hawala collapses and prosecution to understand the very precarious nature of the traditional system and the wider systemic risk they pose to the participants and investors in general.
            </p>
          </div>
        </Card>

        {/* Key Takeaways */}
        <Card className="p-8 md:p-12 mb-8 bg-accent/5 border-accent/20">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
            <Scale className="w-7 h-7 text-accent" />
            KEY TAKEAWAYS
          </h2>
          
          <div className="space-y-6">
            <div className="flex gap-4">
              <ArrowRight className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
              <p className="text-lg">Concentration of international banks offering correspondence banking services</p>
            </div>
            
            <div className="flex gap-4">
              <ArrowRight className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
              <p className="text-lg">And where they are offering the services, the correspondent bank needs to generate sufficient volume and value.</p>
            </div>
          </div>

          <div className="mt-8 p-6 bg-background rounded-lg border">
            <p className="text-sm text-muted-foreground mb-4">
              <strong>Important Notice:</strong> It is the responsibility of the Board of Directors and management of an institution to establish appropriate policies on anti-money laundering and combating the financing of terrorism (AML/CFT) procedures and to train staff to ensure adequate identification of customers, source of funds and the use of the said funds. Such policies should also ensure the effective prevention, detection and control of possible money laundering and terrorism financing.
            </p>
            
            <p className="font-semibold mb-3">With particular focus on the following:</p>
            
            <ul className="space-y-3">
              <li className="flex gap-3">
                <span className="font-bold text-accent">Placement:</span>
                <span>The physical disposal of the initial proceeds derived from illegal activity</span>
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-accent">Layering:</span>
                <span>Separating illicit proceeds from their source by creating complex layers of financial transactions designed to disguise the audit trail and provide anonymity</span>
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-accent">Integration:</span>
                <span>The provision of apparent legitimacy to criminally derived wealth. If the layering process has succeeded, an integration scheme places the laundered proceeds back into the economy in such a way that they re-enter the financial system appearing as normal business funds</span>
              </li>
            </ul>
          </div>
        </Card>

        {/* Partnership Benefits */}
        <Card className="p-8 md:p-12 mb-8">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
            <Users className="w-7 h-7 text-accent" />
            Partnership Resolution & Key Issues
          </h2>
          
          <p className="text-lg mb-6">The partnership will lead to reconciliation and resolution of key issues:</p>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-4 border rounded-lg">
              <div className="flex items-center gap-3 mb-2">
                <Shield className="w-5 h-5 text-accent" />
                <h3 className="font-semibold">Know Your Customer (KYC)</h3>
              </div>
              <p className="text-muted-foreground">Development of insight into the operations and systems deployed and used in client relationship and management.</p>
            </div>
            
            <div className="p-4 border rounded-lg">
              <div className="flex items-center gap-3 mb-2">
                <Globe className="w-5 h-5 text-accent" />
                <h3 className="font-semibold">Legal Entity Identifier (LEI)</h3>
              </div>
              <p className="text-muted-foreground">In all transactions which is basic requirement in mobile wallet and transfer – the instrument and infrastructure deployed.</p>
            </div>
            
            <div className="p-4 border rounded-lg">
              <div className="flex items-center gap-3 mb-2">
                <FileText className="w-5 h-5 text-accent" />
                <h3 className="font-semibold">Information Sharing</h3>
              </div>
              <p className="text-muted-foreground">Recommendation on information-sharing initiatives – over joint deployment and partnership.</p>
            </div>
            
            <div className="p-4 border rounded-lg">
              <div className="flex items-center gap-3 mb-2">
                <Scale className="w-5 h-5 text-accent" />
                <h3 className="font-semibold">Risk Management</h3>
              </div>
              <p className="text-muted-foreground">Pertaining to liquidity management, credit and operations risk and counter party risk.</p>
            </div>
          </div>
          
          <div className="p-4 border rounded-lg mt-6">
            <div className="flex items-center gap-3 mb-2">
              <ArrowRight className="w-5 h-5 text-accent" />
              <h3 className="font-semibold">Payment Messages</h3>
            </div>
            <p className="text-muted-foreground">Recommendation on payment messages for seamless and compliant transactions.</p>
          </div>
        </Card>

        {/* Thank You */}
        <div className="text-center py-8">
          <h2 className="text-3xl font-bold text-accent mb-4">THANK YOU</h2>
          <p className="text-muted-foreground mb-6">For trusting EzyTransfer with your money transfer needs</p>
          
          <div className="flex justify-center gap-4 flex-wrap">
            <Link to="/register">
              <Button className="gap-2">
                Get Started <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link to="/terms">
              <Button variant="outline">Terms of Service</Button>
            </Link>
            <Link to="/privacy">
              <Button variant="outline">Privacy Policy</Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t py-8 mt-12">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
            <Link to="/about" className="hover:text-accent">About</Link>
            <Link to="/terms" className="hover:text-accent">Terms of Service</Link>
            <Link to="/privacy" className="hover:text-accent">Privacy Policy</Link>
            <Link to="/cookies" className="hover:text-accent">Cookie Policy</Link>
          </div>
          <p className="text-center text-sm text-muted-foreground mt-4">
            © {new Date().getFullYear()} Bullaale AS & EzyTransfer. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
